


































import { Vue, Component, Prop } from 'vue-property-decorator';
import LineChart from './charts/LineChart.vue';
import DoughnutChart from './charts/DoughnutChart.vue';
import PieChart from './charts/PieChart.vue';
import HorizontalBarChart from './charts/HorizontalBarChart.vue';
import TicketPagination from './TicketPagination.vue';
import ToggleButton from "@common/vue-toggle-btn/toggle-btn.vue";

import { ChartType } from '../enums/ChartTypes';
import { ChartOptions } from 'chart.js';

@Component({
    components: {
        LineChart, DoughnutChart, PieChart, HorizontalBarChart, ToggleButton, TicketPagination
    }
})
export default class BigDashboardCard extends Vue {
    @Prop({ required: false, default: ChartType.line }) type: ChartType;
    @Prop({ required: true }) labels: string[];
    @Prop({ required: true }) data: number[];

    static backgroundColors = ['#19CEAD', '#857ED4', '#48A5F5', '#DB8DD8', '#FF5249', '#19B817', '#3782F2',
        '#FFDAB9', '#C5C5C5', '#FFFF00', '#C71585', '#DAA520', '#FFFFE0', '#DC143C'];

    showAbsoluteValues = true;
    numElementsOnPage = 6;
    startIndex = 0;
    endIndex = 0;

    toggleOptions = {
        handle: {
            diameter: 17,
            distance: 40
        },
        track : {
            activeColor: "#4CD964",
            width: 32,
            height: 19
        }
    };

    created() {
        this.endIndex = this.data.length;
    }

    getSplitetLabels(str: string, maxwidth = 45) {
        let sections: string[] = [];
        let words = str.split(" ");
        let temp = "";

        words.forEach(function(item, index){
            if(temp.length > 0)
            {
                let concat = temp + ' ' + item;

                if(concat.length > maxwidth){
                    sections.push(temp);
                    temp = "";
                }
                else {
                    if(index == (words.length-1)) {
                        sections.push(concat);
                        return;
                    }
                    else {
                        temp = concat;
                        return;
                    }
                }
            }

            if(index == (words.length-1)) {
                sections.push(item);
                return;
            }

            if(item.length < maxwidth) {
                temp = item;
            }
            else {
                sections.push(item);
            }
        });

        return sections;
    }

    getHorizontalChartLables(labelsWithValues: string[]) {
        return labelsWithValues.slice(this.startIndex, this.endIndex).map(str => {
            return str.length > 45 ? this.getSplitetLabels(str) : str;
        })
    }

    get chartdata() {
        return {
            labels: !this.isLineChart
                ? (this.isHorizontalBarChart ? this.getHorizontalChartLables(this.labelsWithValues) : this.labelsWithValues)
                : this.labels,
            datasets: [
                {
                    ...this.datasetTemplate,
                    data: this.isHorizontalBarChart ? this.processedData.slice(this.startIndex, this.endIndex) : this.processedData,
                    backgroundColor: BigDashboardCard.backgroundColors,
                    maxBarThickness: 12,
                }
            ]
        }
    }

    get processedData() {
        if (this.showAbsoluteValues) {
            return this.data;
        }

        const sum = this.data.reduce((acc, val) => acc + val);

        return this.data.map(item => Math.floor(item / sum * 10000) / 100);
    }

    get options(): ChartOptions {
        const commonProperties = {
            responsive: true,
            maintainAspectRatio: false,
            layout: {
                padding:
                    {
                        top: 20,
                        right: 30,
                        bottom: 20,
                        left: 30
                    }
            }
        };
        switch (this.type) {
            case ChartType.line:
                return {
                    ...commonProperties,
                    legend: {
                        display: false,
                    },
                    scales: {
                        xAxes: [{
                            ticks: {
                                beginAtZero: true,
                                fontColor: '#FFF'
                            },
                            gridLines: {
                                color: 'rgba( 255, 255, 255, 0.5)'
                            }
                        }],
                        yAxes: [{
                            ticks: {
                                fontColor: '#FFF',
                                precision: 0
                            },
                            gridLines: {
                                color: 'rgba( 255, 255, 255, 0.5)'
                            }
                        }],
                    }
                }
            case ChartType.doughnut:
            case ChartType.pie:
                return {
                    ...commonProperties,
                    legend: {
                        display: true,
                        position: 'right',
                        labels: {
                            fontColor: 'white',
                            boxWidth: 12,
                        },
                    },
                    tooltips: {
                        enabled: true,
                        callbacks: {
                            label: (tooltipItem, data) => {
                                if (Array.isArray(data.labels) && tooltipItem.datasetIndex !== undefined && tooltipItem.index !== undefined) {
                                const label = data.labels?.[tooltipItem.index] as string;
                                return label
                                }
                                return ''
                            }
                        }
                    }
                }
            case ChartType.horizontalBar:
                return {
                    ...commonProperties,
                    legend: {
                        display: false,
                    },
                    tooltips: {
                        enabled: false,
                    },
                    scales: {
                        xAxes: [{
                            ticks: {
                                beginAtZero: true,
                                fontColor: '#FFF',
                                precision: 0,
                            },
                            gridLines: {
                                display: false
                            }
                        }],
                        yAxes: [{
                            ticks: {
                                fontColor: '#FFF',
                            },
                            gridLines: {
                                display: false,
                            }
                        }],
                    }
                }
            default:
                return {
                    ...commonProperties
                }
        }
    }

    get isLineChart() {
        return this.type === ChartType.line;
    }

    get isDoughnutChart() {
        return this.type === ChartType.doughnut;
    }

    get isPieChart() {
        return this.type === ChartType.pie;
    }

    get isHorizontalBarChart() {
        return this.type === ChartType.horizontalBar;
    }

    get datasetTemplate() {
        switch (this.type) {
            case ChartType.line:
                return {
                    borderWidth: 3,
                    pointBackgroundColor: '#FFF',
                    pointBorderWidth: 2,
                    pointRadius: 4,
                    borderColor: '#48A5F5',
                    tension: 0,
                    fill: false,
                }
            case ChartType.doughnut:
            case ChartType.pie:
                return {
                    borderWidth: 4,
                    borderColor: '#36373A',
                }
            default:
                return {}
        }
    }

    get labelsWithValues() {
        return this.labels.map((label: string, index: number) =>
            `${this.showAbsoluteValues ? this.processedData[index] : (this.processedData[index]).toFixed(2) + '%'} - ${label}`);
    }

    onToggle() {
        this.showAbsoluteValues = !this.showAbsoluteValues;
    }

    onPageChanged(start: number, end: number) {
        this.startIndex = start;
        this.endIndex = end;
    }
}
